// frontend/src/pages/arena/DuelReactions.jsx
// Emoji reactions floating up the right side of a Live duel's chart, like the Academy live room
// (edu/components/live/Call.jsx ReactionFloat): a 😊 button opens the set, a pick is sent to
// /api/arena/duels/:id/reactions (backend arena_play.py), and everyone in the duel (both players, and spectators of a
// streamed duel) sees new ones rise with the sender's name for about four seconds. Polls every 2 s.
import React, { useEffect, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import { duelFetch } from '../duelIdentity';

const POLL_MS = 2000;

export default function DuelReactions({ duelId, canSend = true, buttonSx }) {
  const [set, setSet] = useState([]);
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState(null);
  const seen = useRef(null);

  useEffect(() => {
    if (!duelId) return undefined;
    let off = false;
    const load = () => duelFetch(`/api/arena/duels/${duelId}/reactions`).then((r) => (r.ok ? r.json() : null)).then((j) => {
      if (off || !j) return;
      setSet(j.set || []);
      const list = j.reactions || [];
      if (seen.current === null) { seen.current = new Set(list.map((r) => r.id)); return; } // don't replay what was there on load
      const fresh = list.filter((r) => !seen.current.has(r.id));
      if (!fresh.length) return;
      fresh.forEach((r) => seen.current.add(r.id));
      setItems((xs) => [...xs, ...fresh.map((r) => ({ ...r, x: 8 + Math.random() * 60 }))].slice(-24));
      const ids = fresh.map((r) => r.id);
      setTimeout(() => { if (!off) setItems((xs) => xs.filter((x) => !ids.includes(x.id))); }, 4200);
    }).catch(() => {});
    load();
    const t = setInterval(() => { if (document.visibilityState === 'visible') load(); }, POLL_MS);
    return () => { off = true; clearInterval(t); };
  }, [duelId]);

  const send = async (emoji) => {
    setOpen(false);
    const r = await duelFetch(`/api/arena/duels/${duelId}/reactions`, 'POST', { emoji }).catch(() => null);
    if (!r || !r.ok) {
      const j = r ? await r.json().catch(() => null) : null;
      setNote(j?.error || 'Could not send that');
      setTimeout(() => setNote(null), 2500);
      return;
    }
    // Show my own straight away; the poll won't replay it.
    const { id } = await r.json().catch(() => ({}));
    if (id) {
      seen.current?.add(id);
      setItems((xs) => [...xs, { id, emoji, name: 'You', x: 8 + Math.random() * 60 }].slice(-24));
      setTimeout(() => setItems((xs) => xs.filter((x) => x.id !== id)), 4200);
    }
  };

  return (
    <>
      {items.length > 0 && (
        <Box aria-live="polite" sx={{ position: 'absolute', right: 70, bottom: 60, width: 120, height: '60%', pointerEvents: 'none', zIndex: 8 }}>
          <style>{'@keyframes duelReact{0%{transform:translateY(0) scale(.6);opacity:0}12%{opacity:1;transform:translateY(-20px) scale(1.1)}100%{transform:translateY(-320px) scale(1);opacity:0}}@media (prefers-reduced-motion:reduce){.duel-react{animation-duration:.01s!important}}'}</style>
          {items.map((r) => (
            <Box key={r.id} component="span" className="duel-react" title={r.name || ''} sx={{ position: 'absolute', bottom: 0, left: `${r.x}%`, fontSize: 30, animation: 'duelReact 4s ease-out forwards', textAlign: 'center' }}>
              {r.emoji}
              {r.name && (
                <Box component="span" sx={{ display: 'block', fontSize: 10.5, fontWeight: 650, color: '#fff', bgcolor: 'rgba(0,0,0,0.55)', borderRadius: '6px', px: '5px', py: '1px', whiteSpace: 'nowrap' }}>
                  {r.mine ? 'You' : String(r.name).split(' ')[0]}
                </Box>
              )}
            </Box>
          ))}
        </Box>
      )}
      {canSend && set.length > 0 && (
        <Box sx={{ position: 'absolute', left: 12, bottom: 40, zIndex: 9, display: 'flex', alignItems: 'center', gap: 1, ...buttonSx }}>
          <Box component="button" type="button" aria-label="React" aria-expanded={open} title="React" onClick={() => setOpen((v) => !v)} sx={{
            width: 38, height: 38, borderRadius: '50%', border: '1px solid rgba(255,255,255,0.14)', bgcolor: 'rgba(20,20,22,0.88)',
            fontSize: 19, cursor: 'pointer', display: 'grid', placeItems: 'center', '&:focus-visible': { outline: '2px solid #0A84FF', outlineOffset: 2 },
          }}>😊</Box>
          {open && (
            <Box role="menu" aria-label="Reactions" sx={{ display: 'flex', gap: '2px', p: '4px', borderRadius: '12px', bgcolor: 'rgba(20,20,22,0.92)', boxShadow: '0 10px 30px rgba(0,0,0,0.35)' }}>
              {set.map((e) => (
                <Box key={e} component="button" type="button" role="menuitem" aria-label={`React ${e}`} onClick={() => send(e)} sx={{
                  font: 'inherit', fontSize: 20, width: 36, height: 36, border: 0, borderRadius: '9px', bgcolor: 'transparent', cursor: 'pointer',
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.08)' },
                }}>{e}</Box>
              ))}
            </Box>
          )}
          {note && <Box role="status" sx={{ fontSize: 12, color: '#fff', bgcolor: 'rgba(0,0,0,0.6)', borderRadius: '8px', px: 1, py: '3px' }}>{note}</Box>}
        </Box>
      )}
    </>
  );
}
